import { type Feature, type Participant } from '../types';
import { Trophy, Award, RotateCcw, DollarSign, Users } from 'lucide-react';

interface ResultsViewProps {
  features: Feature[];
  participants: Participant[];
  onReset: () => void;
}

export function ResultsView({ features, participants, onReset }: ResultsViewProps) {
  // Sort features by total purchases (descending)
  const rankedFeatures = [...features].sort((a, b) => b.purchases - a.purchases);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-[var(--mustard)]" />;
    if (rank === 2) return <Award className="h-6 w-6 text-[var(--text-ter)]" />;
    if (rank === 3) return <Award className="h-6 w-6 text-[var(--mustard)]" />;
    return null;
  };

  const totalSpent = participants.reduce((sum, p) => sum + (p.budget - p.remainingBudget), 0);
  const totalBudget = participants.reduce((sum, p) => sum + p.budget, 0);

  // Detect ties
  const ties = new Map<number, Feature[]>();
  rankedFeatures.forEach((feature) => {
    if (!ties.has(feature.purchases)) {
      ties.set(feature.purchases, []);
    }
    ties.get(feature.purchases)!.push(feature);
  });

  const hasTies = Array.from(ties.values()).some((group) => group.length > 1);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Buy A Feature Results
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Features ranked by number of purchases. Total budget: ${totalBudget.toFixed(2)}, Total spent: ${totalSpent.toFixed(2)}.
        </p>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">Participants</span>
            </div>
            <div className="text-2xl font-bold text-[var(--text-sec)]">{participants.length}</div>
          </div>
          <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-5 w-5 text-[var(--green)]" />
              <span className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Total Budget</span>
            </div>
            <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">${totalBudget.toFixed(2)}</div>
          </div>
          <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-5 w-5 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">Total Spent</span>
            </div>
            <div className="text-2xl font-bold text-[var(--text-sec)]">${totalSpent.toFixed(2)}</div>
          </div>
          <div className="p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="h-5 w-5 text-[var(--mustard)]" />
              <span className="text-sm text-[var(--mustard)]">Features</span>
            </div>
            <div className="text-2xl font-bold text-[var(--mustard)]">{features.length}</div>
          </div>
        </div>

        {/* Ties Warning */}
        {hasTies && (
          <div className="mb-6 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Ties Detected</h3>
            <p className="text-sm text-[var(--mustard)]">
              Some features have the same number of purchases. In Buy A Feature, ties indicate equal priority.
            </p>
            <div className="mt-3 space-y-2">
              {Array.from(ties.entries())
                .filter(([_, group]) => group.length > 1)
                .map(([purchases, group]) => (
                  <div key={purchases} className="text-sm">
                    <strong>{purchases} purchase{purchases !== 1 ? 's' : ''}:</strong> {group.map((f) => f.name).join(', ')}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Ranked List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Ranked Features</h3>
          <div className="space-y-3">
            {rankedFeatures.map((feature, index) => {
              const rank = index + 1;
              const isTied = ties.get(feature.purchases)?.length > 1;

              return (
                <div
                  key={feature.id}
                  className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--rule)] hover:border-[var(--slate)] transition-colors"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] font-bold text-lg text-[var(--text-sec)]">
                    {getRankIcon(rank) || rank}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[var(--text-pri)] text-lg">
                      {feature.name}
                      {isTied && <span className="text-sm text-[var(--text-ter)] ml-2">(tied)</span>}
                    </h3>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm text-[var(--text-sec)]">
                        <strong className="text-[var(--text-pri)]">{feature.purchases}</strong> purchase{feature.purchases !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm text-[var(--text-sec)]">
                        <strong className="text-[var(--text-pri)]">${feature.totalSpent.toFixed(2)}</strong> total spent
                      </span>
                      <span className="text-sm text-[var(--text-ter)]">
                        ${feature.price.toFixed(2)} each
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Purchase Distribution */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Purchase Distribution</h3>
          <div className="bg-[var(--surface)] p-4">
            <div className="space-y-2">
              {rankedFeatures.map((feature) => {
                const maxPurchases = Math.max(...rankedFeatures.map(f => f.purchases), 1);
                return (
                  <div key={feature.id} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[var(--text-sec)]">{feature.name}</span>
                        <span className="font-semibold text-[var(--text-pri)]">{feature.purchases} purchase{feature.purchases !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                        <div
                          className="bg-[var(--slate)] h-2 rounded-full transition-all"
                          style={{
                            width: maxPurchases > 0 ? `${(feature.purchases / maxPurchases) * 100}%` : '0%',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Participant Breakdown */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Participant Purchases</h3>
          <div className="space-y-3">
            {participants.map((participant) => {
              const participantPurchases = participant.purchases
                .map(p => {
                  const feature = features.find(f => f.id === p.featureId);
                  return feature ? { ...p, featureName: feature.name, price: feature.price } : null;
                })
                .filter(Boolean) as Array<{ featureId: string; quantity: number; featureName: string; price: number }>;

              return (
                <div key={participant.id} className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-[var(--text-pri)]">{participant.name}</h4>
                    <span className="text-sm text-[var(--text-sec)]">
                      ${(participant.budget - participant.remainingBudget).toFixed(2)} / ${participant.budget.toFixed(2)}
                    </span>
                  </div>
                  {participantPurchases.length > 0 ? (
                    <div className="space-y-1">
                      {participantPurchases.map((p) => (
                        <div key={p.featureId} className="text-sm text-[var(--text-sec)]">
                          • {p.featureName}: {p.quantity}x @ ${p.price.toFixed(2)} = ${(p.quantity * p.price).toFixed(2)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-[var(--text-ter)] italic">No purchases</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


